import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Alert, AlertDescription } from './ui/alert';
import { Package, Upload, AlertCircle, CheckCircle2, Clock } from 'lucide-react';
import axios from 'axios';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fonction pour obtenir la date/heure actuelle au format datetime-local
const getCurrentDateTime = () => {
  const now = new Date();
  // Format: YYYY-MM-DDTHH:MM
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const ChargementModal = ({ open, onClose, operation, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  
  // Loading info - Pré-rempli avec la date/heure actuelle
  const [loadingStartTime, setLoadingStartTime] = useState(getCurrentDateTime());
  const [loadingLocation, setLoadingLocation] = useState(operation?.departure || '');
  const [loadingResponsible, setLoadingResponsible] = useState('');
  const [loadingComment, setLoadingComment] = useState('');
  
  // Documents
  const [bonChargement, setBonChargement] = useState(null);
  const [photosChargement, setPhotosChargement] = useState([]);

  // Réinitialiser la date/heure à l'ouverture du modal
  useEffect(() => {
    if (open) {
      setLoadingStartTime(getCurrentDateTime());
      setLoadingLocation(operation?.departure || '');
    }
  }, [open, operation?.departure]);

  const handlePhotosChange = (files) => {
    const fileArray = Array.from(files);
    if (fileArray.length === 0) return;
    
    // Check file sizes
    const oversized = fileArray.filter(f => f.size > 10 * 1024 * 1024);
    if (oversized.length > 0) {
      toast({
        title: "Fichiers trop volumineux",
        description: "La taille maximale par photo est de 10MB",
        variant: "destructive"
      });
      return;
    }
    
    setPhotosChargement(fileArray);
  };

  const validateForm = () => {
    if (!loadingStartTime) {
      toast({
        title: "Champ manquant",
        description: "L'heure de début de chargement est obligatoire",
        variant: "destructive"
      });
      return false;
    }
    
    if (!loadingLocation.trim()) {
      toast({
        title: "Champ manquant",
        description: "Le lieu de chargement est obligatoire",
        variant: "destructive"
      });
      return false;
    }
    
    // Responsable sur site - OPTIONNEL
    // Bon de chargement - OPTIONNEL
    // Photos de chargement - OPTIONNEL
    
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      // Step 1: Create logistics info
      const logisticsData = {
        workflowStep: 'en_cours_de_chargement',
        loadingStartTime: new Date(loadingStartTime).toISOString(),
        loadingLocation: loadingLocation,
        loadingResponsible: loadingResponsible,
        loadingComment: loadingComment || null
      };
      
      await axios.post(
        `${API}/operations/${operation.id}/logistics`,
        logisticsData
      );
      
      // Step 2: Upload bon de chargement (si fourni)
      if (bonChargement) {
        const bonFormData = new FormData();
        bonFormData.append('file', bonChargement);
        bonFormData.append('documentType', 'bon_chargement');
        
        await axios.post(
          `${API}/operations/${operation.id}/documents`,
          bonFormData,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
        );
      }
      
      // Step 3: Upload photos
      const photoPromises = photosChargement.map(photo => {
        const formData = new FormData();
        formData.append('file', photo);
        formData.append('documentType', 'photos_chargement');
        
        return axios.post(
          `${API}/operations/${operation.id}/documents`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
        );
      });
      
      await Promise.all(photoPromises);
      
      // Step 4: Change status to EN_COURS_DE_CHARGEMENT (not charge_en_route)
      // Workflow V2: prete_au_chargement → en_cours_de_chargement → charge_en_route
      const { changeOperationStatus } = await import('../utils/statusChangeHelper');
      await changeOperationStatus(operation.id, 'en_cours_de_chargement', {
        loading_start_time: loadingStartTime,
        loading_location: loadingLocation,
        loading_responsible: loadingResponsible,
        loading_comment: loadingComment
      });
      
      toast({
        title: "✅ Chargement enregistré",
        description: "L'opération est maintenant en cours de chargement"
      });
      
      if (onSuccess) {
        onSuccess();
      }
      
      onClose();
      
    } catch (error) {
      console.error('Error submitting chargement:', error);
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Une erreur est survenue",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Début du Chargement</DialogTitle>
          <DialogDescription>
            Enregistrer les informations de début de chargement
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-4">
            <Package className="w-5 h-5 text-[#1db584]" />
            <h3 className="text-lg font-semibold">Informations de Chargement</h3>
          </div>
          
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Tous les champs marqués * sont obligatoires
            </AlertDescription>
          </Alert>
          
          <div className="space-y-3">
            <div>
              <Label htmlFor="loadingStartTime">Heure de début de chargement *</Label>
              <Input
                id="loadingStartTime"
                type="datetime-local"
                value={loadingStartTime}
                onChange={(e) => setLoadingStartTime(e.target.value)}
                required
              />
            </div>
            
            <div>
              <Label htmlFor="loadingLocation">Lieu de chargement *</Label>
              <Input
                id="loadingLocation"
                placeholder="Adresse complète"
                value={loadingLocation}
                onChange={(e) => setLoadingLocation(e.target.value)}
                required
              />
            </div>
            
            <div>
              <Label htmlFor="loadingResponsible">Responsable sur site (optionnel)</Label>
              <Input
                id="loadingResponsible"
                placeholder="Nom du responsable"
                value={loadingResponsible}
                onChange={(e) => setLoadingResponsible(e.target.value)}
              />
            </div>
            
            <div>
              <Label htmlFor="loadingComment">Commentaires (optionnel)</Label>
              <Textarea
                id="loadingComment"
                placeholder="Remarques, observations..."
                value={loadingComment}
                onChange={(e) => setLoadingComment(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          
          <div className="space-y-4 pt-4 border-t">
            <div className="flex items-center gap-2 mb-2">
              <Upload className="w-5 h-5 text-[#1db584]" />
              <h3 className="text-lg font-semibold">Documents</h3>
            </div>
            
            <div className="border rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <Label htmlFor="bonChargement" className="flex items-center gap-2">
                  📄 Bon de chargement (optionnel)
                </Label>
                {bonChargement && <CheckCircle2 className="w-5 h-5 text-green-600" />}
              </div>
              
              <Input
                id="bonChargement"
                type="file"
                accept="image/*,.pdf"
                onChange={(e) => setBonChargement(e.target.files[0])}
                className="cursor-pointer"
              />
              
              {bonChargement && (
                <p className="text-sm text-gray-600 mt-1">
                  {bonChargement.name}
                </p>
              )}
            </div>
            
            <div className="border rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <Label htmlFor="photosChargement" className="flex items-center gap-2">
                  📸 Photos du chargement (optionnel)
                </Label>
                {photosChargement.length > 0 && (
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                )}
              </div>
              
              <Input
                id="photosChargement"
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => handlePhotosChange(e.target.files)}
                className="cursor-pointer"
              />
              
              {photosChargement.length > 0 && (
                <p className="text-sm text-gray-600 mt-1">
                  {photosChargement.length} photo(s) sélectionnée(s)
                </p>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex justify-between pt-4 border-t">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Annuler
          </Button>
          
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? 'Traitement...' : 'Valider le chargement'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ChargementModal;
